/**
 * Widget Dashboard « Ventes du mois » — branché sur `dashboard.ventesMois` (Lot H.3).
 *
 * Donne la tendance commerciale du mois en cours : CA HT cumulé + Sparkline de la
 * série JOURNALIÈRE du mois. Le total complète toujours le graphe (jamais le graphe
 * seul). Aucune comparaison inventée : l'endpoint ne fournit pas de % vs mois
 * précédent, on ne l'affiche donc pas (règle « zéro chiffre inventé »).
 *
 * Gating : masqué sans `vente.read` (fail-closed via `useCan` + `enabled`). Si la
 * requête renvoie tout de même FORBIDDEN (droits périmés), on masque proprement.
 */

import { extractTrpcCode } from "@champ-erp/auth-primitives/web";
import type { VentesMoisSortie } from "@champ-erp/contracts";
import { Card, Skeleton, Sparkline, WidgetHeader } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useCan } from "../../auth/use-can.js";
import { trpc } from "../../lib/trpc.js";
import { formatEurosCents } from "../ventes/shared/format-montants.js";
import { ROUTES_VENTES } from "../ventes/shared/routes.js";

export function WidgetVentesMois() {
  const { granted, isLoading: permLoading } = useCan("vente.read");
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboard.ventesMois"],
    queryFn: () => trpc.dashboard.ventesMois.query(),
    enabled: granted,
    retry: false,
  });

  // Sans la permission ventes, le widget n'existe pas (ni titre, ni cadre vide).
  if (permLoading || !granted) return null;
  // Droits périmés côté serveur → on masque plutôt que d'afficher une erreur rouge.
  if (isError && extractTrpcCode(error) === "FORBIDDEN") return null;

  return (
    <Card as="section" className="p-6">
      <WidgetHeader
        eyebrow="Commerce · Tendance"
        title="Ventes du mois"
        action={{
          label: "Voir les ventes",
          href: ROUTES_VENTES.hub,
          onClick: (event) => {
            event.preventDefault();
            navigate(ROUTES_VENTES.hub);
          },
        }}
      />
      {isLoading ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton variant="rect" height={40} />
          <Skeleton variant="rect" height={56} />
        </div>
      ) : isError || !data ? (
        <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
          Impossible de charger les ventes du mois pour l&apos;instant.
        </p>
      ) : (
        <VentesMoisContenu stats={data} />
      )}
    </Card>
  );
}

function VentesMoisContenu({ stats }: { stats: VentesMoisSortie }) {
  // État vide honnête : aucune vente ce mois → invitation, pas un « 0 € » sec.
  if (stats.nombre_ventes === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
          <TrendingUp size={26} />
        </div>
        <p className="text-[14px] text-champagne-800">Aucune vente ce mois-ci.</p>
        <Link
          to={ROUTES_VENTES.nouveauDocument}
          className="text-[13px] font-medium text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
        >
          Créer un devis →
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="leading-tight">
        <div className="mono tnum text-[30px] font-semibold text-aubergine-900">
          {formatEurosCents(stats.total_ht_cents)}
        </div>
        <p className="mt-1 text-[13px] text-champagne-800">
          HT · <span className="mono tnum">{stats.nombre_ventes}</span> vente
          {stats.nombre_ventes > 1 ? "s" : ""} ce mois-ci
        </p>
      </div>
      {/* Sparkline uniquement si assez de points (le composant se replie sinon). */}
      {stats.serie.length >= 2 && (
        <Sparkline
          points={stats.serie}
          tone="gold"
          width={200}
          height={56}
          ariaLabel="Chiffre d'affaires HT jour par jour du mois en cours"
        />
      )}
    </div>
  );
}
